// Component for searching recipes using items from the user's inventory
// - select ingredients to include in the search
// - perform search and paginate results
// - view full recipe details via a results panel

import {
  ChevronDown,
  ChevronUp,
  CookingPot,
  Eraser,
  Heart,
  ListFilter,
  Search,
  Settings2,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useFavorites } from "../hooks/useFavorites";
import { useInventory } from "../hooks/useInventory";
import {
  groupByCategory,
  loadSortPreference,
  type SortPreference,
  saveSortPreference,
  sortItems,
} from "../lib/sorting";
import {
  type Recipe,
  type RecipeDetails,
  recipeService,
} from "../services/recipeService";
import RecipeResults from "./RecipeResults";
import SearchInput from "./SearchInput";
import SortControl from "./SortControl";

// Main component handling recipe selection, search, and result navigation
export default function RecipeSearch() {
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [ingredientSort, setIngredientSort] = useState<SortPreference>(() =>
    loadSortPreference("recipeIngredientSort"),
  );
  const [selectedIngredientCategories, setSelectedIngredientCategories] =
    useState<string[]>(() => {
      const stored = localStorage.getItem("recipeIngredientFilters");
      return stored ? JSON.parse(stored) : [];
    });
  const [ingredientSearchQuery, setIngredientSearchQuery] = useState("");
  const [showIngredientControls, setShowIngredientControls] = useState(() => {
    const stored = localStorage.getItem("recipeControlsExpanded");
    return stored === null ? true : stored === "true";
  });
  const { items: allItems, isLoading: loadingItems } = useInventory("all");
  const recipeItems = allItems.filter((item) => item.location !== "other");
  const recipeItemNames = new Set(recipeItems.map((item) => item.item_name));
  // Drop any selections whose inventory item no longer exists
  const effectiveSelectedItems = selectedItems.filter((name) =>
    recipeItemNames.has(name),
  );

  // Derive available ingredient categories from all recipe-eligible items for the filter UI
  const availableIngredientCategories = useMemo(() => {
    return [
      ...new Set(
        recipeItems
          .map((item) => item.category)
          .filter((c): c is string => !!c),
      ),
    ].sort();
  }, [recipeItems]);

  // Derive filtered categories to only show those that exist
  const filteredSelectedIngredientCategories = useMemo(() => {
    return selectedIngredientCategories.filter((cat) =>
      availableIngredientCategories.includes(cat),
    );
  }, [selectedIngredientCategories, availableIngredientCategories]);

  const filteredRecipeItems = useMemo(() => {
    const normalizedQuery = ingredientSearchQuery.trim().toLowerCase();
    return recipeItems.filter((item) => {
      const matchesCategory =
        filteredSelectedIngredientCategories.length === 0 ||
        (item.category &&
          filteredSelectedIngredientCategories.includes(item.category));
      const matchesSearch =
        normalizedQuery === "" ||
        item.item_name.toLowerCase().includes(normalizedQuery);
      return matchesCategory && matchesSearch;
    });
  }, [
    recipeItems,
    filteredSelectedIngredientCategories,
    ingredientSearchQuery,
  ]);

  const sortedRecipeItems = useMemo(
    () => sortItems(filteredRecipeItems, ingredientSort),
    [filteredRecipeItems, ingredientSort],
  );
  const groupedRecipeItems = useMemo(
    () =>
      ingredientSort.mode === "category"
        ? groupByCategory(filteredRecipeItems)
        : null,
    [filteredRecipeItems, ingredientSort.mode],
  );

  const handleIngredientSortChange = (pref: SortPreference) => {
    setIngredientSort(pref);
    saveSortPreference("recipeIngredientSort", pref);
  };

  const toggleIngredientCategory = (category: string) => {
    setSelectedIngredientCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    );
  };

  // Persist selected ingredient categories to localStorage
  useEffect(() => {
    localStorage.setItem(
      "recipeIngredientFilters",
      JSON.stringify(selectedIngredientCategories),
    );
  }, [selectedIngredientCategories]);

  // Persist expanded/collapsed state of the sort/filter/search controls
  useEffect(() => {
    localStorage.setItem(
      "recipeControlsExpanded",
      String(showIngredientControls),
    );
  }, [showIngredientControls]);

  const renderIngredientButton = (item: (typeof recipeItems)[number]) => (
    <button
      type="button"
      key={item.id}
      onClick={() => toggleItem(item.item_name)}
      aria-pressed={effectiveSelectedItems.includes(item.item_name)}
      className={`rounded-lg p-3 text-left text-sm font-medium ${
        effectiveSelectedItems.includes(item.item_name)
          ? "shadow-.md bg-green-600 text-white dark:bg-green-600"
          : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
      }`}
    >
      {item.item_name}
      <div className="mt-1 text-xs opacity-75">
        {item.quantity} {item.unit}
      </div>
    </button>
  );
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [preloadedDetails, setPreloadedDetails] =
    useState<RecipeDetails | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const { favorites, removeFavorite } = useFavorites();
  const savedScrollY = useRef(0);

  const toggleItem = (itemName: string) => {
    setSelectedItems((prev) =>
      prev.includes(itemName)
        ? prev.filter((name) => name !== itemName)
        : [...prev, itemName],
    );
  };

  const clearAllSelections = () => {
    setSelectedItems([]);
  };

  const handleSearchRecipes = async () => {
    if (effectiveSelectedItems.length === 0) {
      setError("Please select at least one ingredient");
      return;
    }

    setError("");
    setLoading(true);
    setRecipes([]);
    setHasMore(false);

    try {
      const results = await recipeService.findRecipes(
        effectiveSelectedItems,
        0,
        12,
      );
      setRecipes(results);
      if (results.length === 12) {
        setHasMore(true);
      }
      if (results.length === 0) {
        toast("No recipes found with the selected ingredients.");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to search for recipes";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (loadingMore || loading) return;

    setLoadingMore(true);
    setError("");

    try {
      const newNumber = recipes.length + 12;
      const results = await recipeService.findRecipes(
        effectiveSelectedItems,
        0,
        newNumber,
      );
      const hasMore = results.length === newNumber;
      setRecipes(results);
      setHasMore(hasMore);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load more recipes";
      setError(errorMessage);
    } finally {
      setLoadingMore(false);
    }
  };

  const openFavorite = (fav: (typeof favorites)[number]) => {
    const recipe: Recipe = {
      id: fav.recipe_id,
      title: fav.title,
      image: fav.image ?? "",
      usedIngredients: [],
      missedIngredients: [],
      usedIngredientCount: 0,
      missedIngredientCount: 0,
    };
    setPreloadedDetails(fav.recipe_data);
    setSelectedRecipe(recipe);
  };

  const handleRemoveFavorite = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      await removeFavorite(id);
      toast("Removed from favorites");
    } catch {
      toast.error("Failed to remove favorite");
    }
  };

  if (selectedRecipe) {
    return (
      <RecipeResults
        recipe={selectedRecipe}
        onBack={() => {
          setSelectedRecipe(null);
          setPreloadedDetails(null);
          // Restore scroll position after React re-renders the results list
          requestAnimationFrame(() => {
            window.scrollTo({ top: savedScrollY.current, behavior: "instant" });
          });
        }}
        preloadedDetails={preloadedDetails ?? undefined}
      />
    );
  }

  return (
    <div className="space-y-6">
      <output className="sr-only" aria-live="polite" aria-atomic="true">
        {loading ? "Searching for recipes..." : ""}
        {recipes.length > 0 && !loading
          ? `Found ${recipes.length} recipe${recipes.length !== 1 ? "s" : ""}`
          : ""}
        {error ? `Error: ${error}` : ""}
      </output>
      <div>
        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
          Find Recipes
        </h2>
        <p className="mt-1 text-gray-600 dark:text-gray-400">
          Select ingredients from your inventory to find recipes
        </p>
      </div>

      {favorites.length > 0 && (
        <div className="rounded-lg bg-white p-6 shadow-sm dark:bg-gray-800">
          <h3 className="mb-4 flex items-center gap-2 text-xl font-bold text-gray-800 dark:text-gray-100">
            <Heart
              className="h-5 w-5 fill-pink-500 text-pink-500"
              aria-hidden="true"
            />
            Your Favorites
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {favorites.map((fav) => (
              <article
                key={fav.id}
                className="group relative flex h-full flex-col overflow-hidden rounded-lg shadow-sm hover:shadow-md"
              >
                <button
                  type="button"
                  onClick={() => {
                    savedScrollY.current = window.scrollY;
                    openFavorite(fav);
                  }}
                  className="flex h-full flex-col text-left"
                  aria-label={`View favorite recipe: ${fav.title}`}
                >
                  <div className="h-48 overflow-hidden rounded-t-lg bg-gray-200 dark:bg-gray-700">
                    {fav.image && (
                      <img
                        src={fav.image}
                        alt=""
                        className="h-full w-full object-cover transition group-hover:scale-103"
                      />
                    )}
                  </div>
                  <div className="flex flex-1 flex-col justify-between rounded-b-lg bg-gray-50 p-4 dark:bg-gray-700">
                    <h3 className="line-clamp-2 font-medium text-gray-800 group-hover:text-green-600 dark:text-gray-100 dark:group-hover:text-green-400">
                      {fav.title}
                    </h3>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={(e) => handleRemoveFavorite(e, fav.id)}
                  aria-label={`Remove ${fav.title} from favorites`}
                  className="absolute top-2 right-2 rounded-full bg-white/80 p-1.5 text-pink-600 hover:bg-white dark:bg-gray-800/80 dark:text-pink-400 dark:hover:bg-gray-800"
                >
                  <Heart className="h-4 w-4 fill-current" aria-hidden="true" />
                </button>
              </article>
            ))}
          </div>
        </div>
      )}

      {recipeItems.length > 0 && (
        <div className="rounded-lg bg-white p-6 shadow-sm dark:bg-gray-800">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-xl font-semibold text-gray-800 dark:text-gray-100">
              <Settings2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              Item Controls
            </h3>
            <button
              type="button"
              onClick={() => setShowIngredientControls((v) => !v)}
              className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
              aria-expanded={showIngredientControls}
              aria-controls="recipe-controls-panel"
              aria-label={
                showIngredientControls
                  ? "Collapse sort, filter, and search options"
                  : "Expand sort, filter, and search options"
              }
            >
              {showIngredientControls ? (
                <ChevronUp className="h-5 w-5" aria-hidden="true" />
              ) : (
                <ChevronDown className="h-5 w-5" aria-hidden="true" />
              )}
            </button>
          </div>

          {showIngredientControls && (
            <div id="recipe-controls-panel" className="mt-6 space-y-6">
              <SortControl
                value={ingredientSort}
                onChange={handleIngredientSortChange}
                legend="Ingredient sort order"
              />

              {availableIngredientCategories.length > 0 && (
                <fieldset className="border-t border-gray-200 pt-6 dark:border-gray-700">
                  <legend className="sr-only">Category Filter</legend>
                  <div className="mb-4 flex items-center justify-between gap-2">
                    <h3 className="flex items-center gap-2 text-xl font-bold text-gray-800 dark:text-gray-100">
                      <ListFilter className="h-5 w-5 text-blue-500 dark:text-blue-400" />
                      Filter:
                    </h3>
                    <button
                      type="button"
                      onClick={() => setSelectedIngredientCategories([])}
                      className={`flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium transition ${
                        filteredSelectedIngredientCategories.length > 0
                          ? "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                          : "pointer-events-none invisible opacity-0"
                      }`}
                      aria-label="Clear all filters"
                    >
                      <Eraser className="h-4 w-4" aria-hidden="true" />
                      Clear All
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {availableIngredientCategories.map((cat) => (
                      <button
                        type="button"
                        key={cat}
                        onClick={() => toggleIngredientCategory(cat)}
                        aria-pressed={filteredSelectedIngredientCategories.includes(
                          cat,
                        )}
                        className={`rounded-full px-3 py-1 text-sm font-medium capitalize transition ${
                          filteredSelectedIngredientCategories.includes(cat)
                            ? "bg-blue-600 text-white dark:bg-blue-500"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                        }`}
                      >
                        {cat.replace("_", " ")}
                      </button>
                    ))}
                  </div>
                </fieldset>
              )}

              <SearchInput
                id="recipe-ingredient-search"
                label="Search your ingredients"
                placeholder="Search items..."
                value={ingredientSearchQuery}
                onChange={setIngredientSearchQuery}
              />
            </div>
          )}
        </div>
      )}

      <div className="rounded-lg bg-white p-6 shadow-sm dark:bg-gray-800">
        <div className="mb-4 flex items-start justify-between gap-2">
          <h3 className="flex items-center gap-2 text-xl font-bold text-gray-800 dark:text-gray-100">
            <CookingPot className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            Your Ingredients
          </h3>

          <button
            type="button"
            onClick={clearAllSelections}
            className={`flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium ${
              selectedItems.length > 0
                ? "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                : "pointer-events-none invisible opacity-0"
            }`}
            aria-label="Clear all selections"
          >
            <Eraser className="h-4 w-4" aria-hidden="true" />
            Clear All
          </button>
        </div>

        {loadingItems ? (
          <output
            className="flex items-center justify-center py-4"
            aria-live="polite"
          >
            <div
              className="h-8 w-8 animate-spin rounded-full border-4 border-green-200 border-t-green-600 dark:border-green-700 dark:border-t-green-400"
              aria-hidden="true"
            ></div>
            <span className="sr-only">Loading your ingredients...</span>
          </output>
        ) : filteredRecipeItems.length === 0 ? (
          <div className="py-8 text-center text-gray-500 dark:text-gray-400">
            <p>
              {recipeItems.length === 0
                ? "No items in your inventory yet"
                : "No items match your filters"}
            </p>
          </div>
        ) : groupedRecipeItems ? (
          <fieldset className="space-y-5">
            <legend className="sr-only">
              Select ingredients for recipe search
            </legend>
            {groupedRecipeItems.map((group) => (
              <div key={group.category}>
                <h4 className="mb-2 text-xs font-bold tracking-wide text-gray-500 uppercase dark:text-gray-400">
                  {group.category.replace("_", " ")}
                </h4>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                  {group.items.map((item) => renderIngredientButton(item))}
                </div>
              </div>
            ))}
          </fieldset>
        ) : (
          <fieldset className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
            <legend className="sr-only">
              Select ingredients for recipe search
            </legend>
            {sortedRecipeItems.map((item) => renderIngredientButton(item))}
          </fieldset>
        )}

        {effectiveSelectedItems.length > 0 && (
          <div className="mt-6 rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-700 dark:bg-green-900/30">
            <p className="text-sm text-green-800 dark:text-green-200">
              {effectiveSelectedItems.length} ingredient
              {effectiveSelectedItems.length !== 1 ? "s" : ""} selected
            </p>
          </div>
        )}

        {error && (
          <div
            role="alert"
            className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-700 dark:bg-red-900/30 dark:text-red-400"
          >
            {error}
          </div>
        )}

        {allItems.length > 0 && (
          <button
            type="button"
            onClick={handleSearchRecipes}
            disabled={loading || effectiveSelectedItems.length === 0}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-green-600 py-3 font-medium text-white transition-opacity hover:bg-green-700 disabled:pointer-events-none disabled:opacity-50 dark:bg-green-600 dark:hover:bg-green-700"
            aria-label={`Search recipes with ${effectiveSelectedItems.length} ingredient${effectiveSelectedItems.length !== 1 ? "s" : ""}`}
          >
            <Search className="h-5 w-5" aria-hidden="true" />
            {loading ? "Searching..." : "Search Recipes"}
          </button>
        )}
      </div>

      {recipes.length > 0 && (
        <div className="rounded-lg bg-white p-6 shadow-sm dark:bg-gray-800">
          <div className="mb-4 flex flex-col gap-1">
            <h3 className="font-semibold text-gray-800 dark:text-gray-100">
              Found {recipes.length} Recipe{recipes.length !== 1 ? "s" : ""}
            </h3>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Powered by the{" "}
              <a
                href="https://spoonacular.com/food-api"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-gray-600 dark:hover:text-gray-300"
              >
                Spoonacular Food API
              </a>
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {recipes.map((recipe) => (
              <article
                key={recipe.id}
                className="group flex h-full flex-col overflow-hidden rounded-lg shadow-sm hover:shadow-md"
              >
                <button
                  type="button"
                  onClick={() => {
                    savedScrollY.current = window.scrollY;
                    setSelectedRecipe(recipe);
                  }}
                  className="flex h-full flex-col text-left"
                  aria-label={`View recipe for ${recipe.title}. Uses ${recipe.usedIngredientCount} of your ingredients${recipe.missedIngredientCount > 0 ? `, needs ${recipe.missedIngredientCount} more` : ""}`}
                >
                  <div className="relative h-48 overflow-hidden rounded-t-lg bg-gray-200 dark:bg-gray-700">
                    <img
                      src={recipe.image}
                      alt=""
                      className="h-full w-full object-cover transition group-hover:scale-103"
                    />
                    <div
                      className="absolute top-2 right-2 rounded-full bg-green-600 px-3 py-1 text-xs font-medium text-white dark:bg-green-600"
                      aria-hidden="true"
                    >
                      {recipe.usedIngredientCount} of your ingredients
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col justify-between rounded-b-lg bg-gray-50 p-4 dark:bg-gray-700">
                    <h3 className="line-clamp-2 font-medium text-gray-800 group-hover:text-green-600 dark:text-gray-100 dark:group-hover:text-green-400">
                      {recipe.title}
                    </h3>
                    {recipe.missedIngredientCount > 0 && (
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        Need {recipe.missedIngredientCount} more ingredient
                        {recipe.missedIngredientCount !== 1 ? "s" : ""}
                      </p>
                    )}
                  </div>
                </button>
              </article>
            ))}
          </div>
          {hasMore && (
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={handleLoadMore}
                disabled={loading || loadingMore}
                className="rounded-lg bg-green-600 px-4 py-2 font-medium text-white transition-opacity hover:bg-green-700 disabled:pointer-events-none disabled:opacity-50 dark:bg-green-600 dark:hover:bg-green-700"
              >
                {loadingMore ? "Loading..." : "Load More Recipes"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
